import LayoutInside from '@/layout/LayoutInside'

import Blocks from '@/assets/icons/Blocks'
import Title from '@/components/shared/Title/Title'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowRight, Calendar } from 'lucide-react'
import Checked from '@/assets/icons/Checked'
import Unchecked from '@/assets/icons/Unchecked'
import { Button } from '@/components/ui/button'
import Security from '@/assets/icons/Security'
import Notifications from '@/assets/icons/Notifications'
import useModalStore from '@/store/useModalStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ProfileService } from '@/services/profile.service'
import { AuthService } from '@/services/auth.service'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateToMonthYear } from '@/lib/utils'
import { GenericModal } from '@/components/Onboarding/GenericModal'
import Loader from '@/components/Loader/Loader'
import { AcquirerStep } from '@/components/Onboarding/Steps/AcquirerStep'
import { ProfileStep } from '@/components/Onboarding/Steps/ProfileStep'
import { AddressStep } from '@/components/Onboarding/Steps/AddressStep'
import { BankStep } from '@/components/Onboarding/Steps/BankStep'
import { DocumentStep } from '@/components/Onboarding/Steps/DocumentStep'
import { useAuth } from '@/auth/Auth'
import { usePermissions } from '@/auth/PermissionsContext'
import * as VeriffSDK from '@veriff/js-sdk'
import { toast } from '@/hooks/use-toast'
import { isOnboardingIdentityVerificationRequired } from '@/utils/onboardingIdentityVerification'
import { IProfile } from '@/@types/Onboarding'
import { isAxiosError } from 'axios'
const Veriff = (VeriffSDK as any).Veriff || VeriffSDK

export default function Onboarding() {
  const { modals, openModal, closeModal } = useModalStore()
  const { authData } = useAuth()
  const { isPrimary } = usePermissions()
  const { t } = useTranslation('onboarding')
  const navigate = useNavigate()
  const identityVerificationRequired = isOnboardingIdentityVerificationRequired()

  const queryClient = useQueryClient()

  const { data: userInfo, isPending: isLoadingUserInfo } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => await AuthService.userInfo(),
  })

  const { data: profile, isPending: isLoadingProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      try {
        return await ProfileService.getProfile()
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 403) {
          return null
        }
        throw error
      }
    },
    retry: false,
  })

  const isOnboardingFinished =
    userInfo?.onboarding_step === 'finished' ||
    (!identityVerificationRequired &&
      userInfo?.onboarding_step === 'identity_verification')

  useEffect(() => {
    if (isLoadingUserInfo) return
    if (isOnboardingFinished) {
      navigate('/my-account', { replace: true })
    }
  }, [isLoadingUserInfo, isOnboardingFinished, navigate])

  const profileStatus = userInfo?.onboarding_detail?.profile
  const documentType = profile?.profile_type
  const isProfileEditing = Boolean(profile || profileStatus)

  const breadcrumbItems = [{ text: t('my_account'), link: '/onboarding' }]

  type MainInfoStep = {
    id: number
    title: string
    onClick: () => void
    isChecked?: boolean
    isDisabled: boolean
  }

  const mainInfoSteps: MainInfoStep[] = [
    {
      id: 1,
      title: t('profile_identity'),
      onClick: () => openModal('profile'),
      isChecked: userInfo?.onboarding_detail?.profile,
      isDisabled: false,
    },
    {
      id: 2,
      title: t('address'),
      onClick: () => openModal('address'),
      isChecked: userInfo?.onboarding_detail?.address,
      isDisabled: false,
    },
  ]

  if (isPrimary) {
    mainInfoSteps.push({
      id: 3,
      title: t('bank_account'),
      isChecked: userInfo?.onboarding_detail?.bankAccount,
      onClick: () => openModal('bank'),
      isDisabled: false,
    })
  }

  mainInfoSteps.push({
    id: 4,
    title: t('documents'),
    isChecked: userInfo?.onboarding_detail?.document,
    onClick: () => openModal('document'),
    isDisabled:
      !userInfo?.onboarding_detail?.profile ||
      !userInfo?.onboarding_detail?.address ||
      (isPrimary && !userInfo?.onboarding_detail?.bankAccount) ||
      Boolean(userInfo?.onboarding_detail?.document),
  })

  const otherInfos = [
    {
      id: 6,
      title: t('notifications'),
      icon: <Notifications />,
      isDisabled: true,
    },
    {
      id: 7,
      title: t('security'),
      icon: <Security />,
      isDisabled: true,
    },
  ]

  const completedSteps = mainInfoSteps?.filter((step) => step.isChecked).length
  const identityVerified = userInfo?.kyc_status === 'approved'
  const baseStepsTotal = mainInfoSteps.length
  const totalSteps = identityVerificationRequired
    ? baseStepsTotal + 1
    : baseStepsTotal
  const completedCount =
    completedSteps + (identityVerificationRequired && identityVerified ? 1 : 0)

  const getFinishedModalSeenKey = (userId?: string | number | null) =>
    `onboarding_finished_modal_seen:${String(userId ?? 'unknown')}`

  const completingOnboarding = async () => {
    await queryClient.invalidateQueries({ queryKey: ['user-info'] })
    openModal('finished_onboarding')
  }

  useEffect(() => {
    // Evita loop: mostra o modal de "cadastro finalizado" apenas uma vez por usuário/dispositivo
    if (!userInfo?.id) return
    const seenKey = getFinishedModalSeenKey(userInfo.id)
    const hasSeenFinishedModal =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(seenKey) === 'true'
    if (hasSeenFinishedModal) return

    if (identityVerificationRequired) {
      if (completedSteps === baseStepsTotal && identityVerified) {
        completingOnboarding()
      }
      return
    }

    // Sem verificação de identidade: conclui quando todos os passos listados estão ok.
    if (completedSteps === baseStepsTotal) {
      completingOnboarding()
    }
  }, [
    completedSteps,
    identityVerified,
    identityVerificationRequired,
    userInfo?.id,
  ])

  const handleUnderstand = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(getFinishedModalSeenKey(userInfo?.id), 'true')
    }
    closeModal('finished_onboarding')
    closeModal('onboarding-completed')
    navigate('/dashboard')
  }

  const handleStartVeriff = async () => {
    try {
      const veriff = Veriff({
        apiKey: import.meta.env.VITE_VERIFF_API_KEY,
        parentId: 'veriff-root',
        onSession: function (
          err: { status?: { text?: string } } | null,
          response: { verification?: { url?: string } },
        ) {
          if (err) {
            console.error('Veriff session error:', err)
            toast({
              variant: 'error',
              description: err.status?.text ?? t('veriff_initialization_error'),
            })
            return
          }
          if (response.verification?.url) {
            window.location.href = response.verification.url
          }
        },
      })

      veriff.setParams({
        person: {
          givenName: ' ',
          lastName: ' ',
        },
        vendorData: userInfo?.id,
      })

      veriff.mount({
        submitBtnText: t('start_verification'),
      })
    } catch (error) {
      console.error('Veriff initialization error:', error)
    }
  }

  return (
    <LayoutInside items={breadcrumbItems}>
      <GenericModal
        modalKey="finished_onboarding"
        title={t('finishedOnboarding.title')}
        description={t('finishedOnboarding.description')}
        confirmButtonText={t('finishedOnboarding.confirmButtonText')}
        onConfirm={handleUnderstand}
        onCancel={handleUnderstand}
      />
      <ProfileStep
        isPending={Boolean(
          profileStatus ?? userInfo?.onboarding_detail?.profile,
        )}
        onClose={() => closeModal('profile')}
        open={modals['profile']}
        profile={profile ?? ({} as IProfile)}
        isEditing={isProfileEditing}
        rejectionReason={userInfo?.onboarding_rejection_reason}
        isProfileRejected={userInfo?.profile_approval_status === 'rejected'}
        isOnboarding
        isProfileStepComplete={Boolean(
          profileStatus ?? userInfo?.onboarding_detail?.profile,
        )}
      />
      <AddressStep
        isPending={Boolean(userInfo?.onboarding_detail?.address)}
        open={modals['address']}
        onClose={() => closeModal('address')}
        isEditing={!!userInfo?.onboarding_detail?.address}
      />
      <BankStep
        key={String(authData?.id ?? '')}
        isPending={Boolean(userInfo?.onboarding_detail?.bankAccount)}
        open={modals['bank']}
        onClose={() => closeModal('bank')}
        isEditing={!!userInfo?.onboarding_detail?.bankAccount}
        isOnboarding
      />
      <DocumentStep
        documentType={documentType}
        isPending={userInfo?.onboarding_detail?.document}
        open={modals['document']}
        onClose={() => closeModal('document')}
      />
      {isLoadingUserInfo || isLoadingProfile ? (
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <Loader />
        </div>
      ) : (
        <section className="flex flex-col items-center">
          <Blocks />
          <Title className="mt-4 !text-xl" text={t('finish_your_board')} />
          <p className="text-muted lg:w-[300px] mt-1 text-sm text-center">
            {t('complete_your_registration')}
          </p>
          <Card className="mt-8 w-full bg-bgDark lg:w-[504px] rounded-xl">
            <CardHeader className="flex p-4 border-b-colorBorder border-b">
              <div className="flex gap-4">
                <p className="font-medium">{t('finished_steps')}</p>
                <Badge variant="secondary" className="text-xs">
                  {completedCount} / {totalSteps}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="px-4 py-2 space-y-2">
              {mainInfoSteps.map((step) => (
                <li key={step.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-4 h-14">
                    {step.isChecked ? <Checked /> : <Unchecked />}
                    <p className="font-medium">{step.title}</p>
                  </div>
                  <Button
                    disabled={step.isDisabled}
                    onClick={step.onClick}
                    variant="outline"
                    size="icon"
                    className="h-9 w-12"
                  >
                    <ArrowRight />
                  </Button>
                </li>
              ))}

              {/* Verificação da adquirente: uma linha por provedor com KYC hospedado. Some
                  sozinho quando o vendedor não tem nenhum. */}
              <AcquirerStep />

              {identityVerificationRequired && (
                <div className="w-full">
                  <li className="flex items-center justify-between">
                    <div className="flex items-center gap-4 h-14">
                      {identityVerified ? (
                        <Checked />
                      ) : (
                        <Unchecked className="text-red-500" />
                      )}
                      <p className="font-medium">
                        {t('identity_verification')}
                      </p>
                    </div>
                    <Button
                      onClick={handleStartVeriff}
                      variant="outline"
                      size="icon"
                      className="h-9 w-12"
                      disabled={
                        userInfo?.onboarding_detail?.document !== true ||
                        identityVerified
                      }
                    >
                      <ArrowRight />
                    </Button>
                  </li>
                  <div className="w-full flex items-center justify-center m-auto">
                    <div
                      id="veriff-root"
                      className="flex items-center justify-center"
                    ></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="lg:w-[504px] bg-bgDark w-full mt-8">
            <CardContent className="py-2 px-4 space-y-2">
              {otherInfos.map((info) => (
                <li key={info.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-4 h-14">
                    {info.icon}
                    <p className="font-semibold">{info.title}</p>
                  </div>
                  <Button
                    disabled={info.isDisabled}
                    variant="outline"
                    size="icon"
                    className="h-9 w-12"
                  >
                    <ArrowRight />
                  </Button>
                </li>
              ))}
            </CardContent>
            <CardFooter className="w-full flex justify-center items-center border-t border-t-colorBorder p-4">
              <Badge
                variant="secondary"
                className="text-sm font-medium gap-2 flex items-center justify-center"
              >
                <Calendar color="#A1A1AA" size={16} />
                {t('member_since')},{' '}
                {formatDateToMonthYear(userInfo?.created_at ?? '')}
              </Badge>
            </CardFooter>
          </Card>
        </section>
      )}
    </LayoutInside>
  )
}
